import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { AssignmentPrecinct } from 'teacher-ui-client/models/assignment'
import type { SchoolScope, StudentScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface SummarySchoolReResponse {
  summary: {
    students: {
      student_id: number
      total_time: number
      sessions: number
      usage_score: number
      lessons_completed: Nullable<number>
      awards_earned_bronze: Nullable<number>
      awards_earned_gold: Nullable<number>
      awards_earned_silver: Nullable<number>
      books_read: Nullable<number>
      current_lesson_lexile: Nullable<number>
      current_lesson_number: Nullable<number>
      driving_tests_attempted: Nullable<number>
      driving_tests_completed: Nullable<number>
      initial_lesson_lexile: Nullable<number>
      initial_lesson_number: Nullable<number>
      lexile_growth: Nullable<number>
      map_quizzes_attempted: Nullable<number>
      map_quizzes_average_score_percent: Nullable<number>
      spelling_completed: Nullable<number>
      stories_written: Nullable<number>
      skills_mastered: Nullable<number>
    }[]
    school: {
      student_count: number
      total_time: number
      stories_written: number
      lessons_completed: number
      books_read: number
      driving_tests_attempted: number
      driving_tests_completed: number
      spelling_completed: number
      awards_earned_gold: number
      awards_earned_silver: number
      awards_earned_bronze: number
      map_quizzes_attempted: number
      skills_mastered_total: number
      average_current_lesson_lexile: Nullable<number>
      average_initial_lesson_lexile: Nullable<number>
      average_lexile_growth: Nullable<number>
      average_usage_score: Nullable<number>
      map_quizzes_average_score_percent: Nullable<number>
    }
  }
}

export type SummarySchoolReData = SummarySchoolReResponse['summary']

export async function summarySchoolRe(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SummarySchoolReData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummarySchoolReResponse>(url, query)
  if (!data) return null

  const { summary } = data
  if (summary.students.length === 0) return null

  return summary
}

export interface SummarySchoolRexResponse {
  summary: {
    students: {
      student_id: number
      sessions: number
      total_time: number
      usage_score: number
      spelling_completed: Nullable<number>
      lexile_growth: Nullable<number>
      lesson_quizzes_attempted: Nullable<number>
      initial_lesson_number: Nullable<number>
      assessments_completed: Nullable<number>
      books_read: Nullable<number>
      current_lesson_number: Nullable<number>
      stadium_events: Nullable<number>
      initial_lesson_lexile: Nullable<number>
      lesson_quizzes_average_score_percent: Nullable<number>
      lessons_completed: Nullable<number>
      assessments_complete_fiction_last_lexile: Nullable<number>
      assessments_complete_nonfiction_last_lexile: Nullable<number>
      current_lesson_lexile: Nullable<number>
      assessments_complete_fiction_last_percentage: Nullable<number>
      assessments_complete_nonfiction_last_percentage: Nullable<number>
    }[]
    school: {
      student_count: number
      total_time: number
      lessons_completed: number
      lesson_quizzes_attempted: number
      assessments_completed: number
      books_read: number
      spelling_completed: number
      stadium_events: number
      average_lexile_growth: Nullable<number>
      average_usage_score: Nullable<number>
      lesson_quizzes_average_score_percent: Nullable<number>
      average_current_lesson_lexile: Nullable<number>
      average_initial_lesson_lexile: Nullable<number>
    }
  }
}

export type SummarySchoolRexData = SummarySchoolRexResponse['summary']

export async function summarySchoolRex(
  this: StudentEventsService,
  product: LegacyProductRex,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SummarySchoolRexData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummarySchoolRexResponse>(url, query)
  if (!data) return null

  const { summary } = data

  if (summary.students.length === 0) return null

  return summary
}

export interface SummarySchoolMsResponse {
  summary: {
    students: {
      student_id: number
      total_time: number
      usage_score: number
      lessons_completed: number
      skills_mastered: number
      sprints_mastered: number
      initial_lesson_number: Nullable<number>
      driving_tests_completed: number
      driving_tests_attempted: number
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      current_lesson_number: Nullable<number>
      map_quizzes_attempted: number
      map_quizzes_taken: number
      map_quizzes_completed: number
      map_quizzes_average_score_percent: Nullable<number>
    }[]
    school: {
      student_count: number
      total_time: number
      lessons_completed: number
      driving_tests_completed: number
      driving_tests_attempted: number
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      map_quizzes_attempted: number
      map_quizzes_taken: number
      map_quizzes_completed: number
      skills_mastered_total: number
      sprints_mastered_total: number
      map_quizzes_average_score_percent: Nullable<number>
      average_usage_score: Nullable<number>
    }
  }
}

export type SummarySchoolMsData = SummarySchoolMsResponse['summary']

export async function summarySchoolMs(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SummarySchoolMsData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummarySchoolMsResponse>(url, query)
  if (!data) return null

  const { summary } = data

  if (summary.students.length === 0) return null

  return summary
}

export interface SummarySchoolFpResponse {
  summary: {
    students: {
      student_id: number
      total_time: number
      usage_score: number
      books_read: Nullable<number>
      lessons_completed: Nullable<number>
      lesson_quizzes_attempted: Nullable<number>
      lesson_quizzes_taken: Nullable<number>
      initial_lesson_number: Nullable<number>
      highest_lesson_number: Nullable<number>
      sound_gems_earned: Nullable<number>
      word_gems_earned: Nullable<number>
      awards_earned_bronze: Nullable<number>
      awards_earned_silver: Nullable<number>
      awards_earned_gold: Nullable<number>
      lesson_quizzes_average_score: Nullable<number>
      lesson_quizzes_total_score: Nullable<number>
    }[]
    school: {
      student_count: number
      total_time: number
      lessons_completed: number
      lesson_quizzes_attempted: number
      lesson_quizzes_taken: number
      books_read: number
      sound_gems_earned: number
      word_gems_earned: number
      awards_earned_bronze: number
      awards_earned_silver: number
      awards_earned_gold: number
      lesson_quizzes_average_score: number
      usage_score: number
      lesson_quizzes_attempted_average: Nullable<number>
      lesson_quizzes_taken_average: Nullable<number>
      lessons_completed_average: Nullable<number>
    }
  }
}

export type SummarySchoolFpData = SummarySchoolFpResponse['summary']

export async function summarySchoolFp(
  this: StudentEventsService,
  product: LegacyProductFp,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SummarySchoolFpData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummarySchoolFpResponse>(url, query)
  if (!data) return null

  const { summary } = data

  if (summary.students.length === 0) return null

  return summary
}

interface Awards {
  bronze: number
  silver: number
  gold: number
}

interface AreasOfWeaknessItem {
  created_at: number
  lesson: number
  score: Nullable<number>
}

interface PlacementTests {
  created_at: number
  lesson: number
  skipped_test: boolean
}

export interface AssignmentsRecentActivityItemRe {
  created_at: number
  score: Nullable<number>
  category: AssignmentPrecinct
  quiz: Nullable<number>
  lesson: number
}

export interface AssignmentsRecentActivityDrivingTestItemRe {
  created_at: number
  score: Nullable<number>
  category: AssignmentPrecinct
  quiz: Nullable<number>
  lesson: null
  driving_test_category: Nullable<string>
}

export interface SummaryStudentsReResponse {
  summary: {
    assessments: {
      attempts: number
      average_score: Nullable<number>
      quizzes_taken: number
      recent_activity: {
        created_at: number
        map_number: number
        score: number
      }[]
    }
    assignments: {
      recent_activity: (AssignmentsRecentActivityItemRe | AssignmentsRecentActivityDrivingTestItemRe)[]
    }
    awards: Awards
    books: {
      fiction: {
        books_read: number
        words_read: number
      }
      nonfiction: {
        books_read: number
        words_read: number
      }
      recent_activity: {
        book_name: string
        created_at: number
        category: string
        lexile: number
      }[]
    }
    driving_tests: {
      recent_activity: {
        category: string
        correct: number
        created_at: number
        quiz: number
        total: number
      }[]
      totals_by_category: {
        category: string
        completed: number
      }[]
    }
    lexiles: {
      growth: number
      highest: number
    }
    reading_lessons: {
      initial_lesson: Nullable<number>
      highest_lesson: Nullable<number>
      total_lessons: number
      total_lessons_mastered: number
      areas_of_weakness: AreasOfWeaknessItem[]
      recent_activity: {
        created_at: number
        lesson: number
        score: Nullable<number>
      }[]
    }
    reading_placement_tests: PlacementTests[]
    spelling_lessons: {
      average_score: Nullable<number>
      highest_lesson: Nullable<number>
      total_lessons: number
      total_lessons_mastered: number
      areas_of_weakness: {
        created_at: number
        lesson: number
        score: number
      }[]
      recent_activity: {
        created_at: number
        lesson: number
      }[]
    }
    spelling_placement_tests: PlacementTests[]
    stories_written: {
      contest_name: string
      created_at: number
      story_id: number
      title: string
    }[]
    storylands: {
      total_lessons: number
      highest_lesson: Nullable<number>
      recent_activity: {
        attempts: number
        created_at: number
        lesson: number
        score: number
      }[]
    }
  }[]
}

export type SummaryStudentsReData = SummaryStudentsReResponse['summary']

export async function summaryStudentsRe(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: StudentScope,
  period: NormalizedInterval,
): Promise<Nullable<SummaryStudentsReData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummaryStudentsReResponse>(url, query)
  if (!data) return null

  const { summary } = data
  if (summary.length === 0) return null

  return summary
}

export interface DrivingTestsCategories {
  category: string
  completed: number
  total_available_tests: number
}

export interface AssignmentsRecentActivityItemMs {
  event_type: string
  created_at: number
  precinct: string
  quiz: Nullable<number>
  lesson: number
  score: Nullable<number>
  grade_position?: number
}

export interface AssignmentsRecentActivityDrivingTestItemMs {
  event_type: string
  created_at: number
  precinct: string
  quiz: Nullable<number>
  lesson: null
  score: Nullable<number>
  grade_position?: number
  driving_test_category: Nullable<string>
  driving_test_grade: number
}

export interface SummaryStudentsMsResponse {
  summary: {
    driving_tests: {
      totals_by_grade: {
        driving_test_grade: number
        categories: DrivingTestsCategories[]
      }[]
      recent_activity: {
        created_at: number
        grade_position: Nullable<number>
        category: string
        total: number
        quiz: number
        driving_test_grade: number
        correct: number
      }[]
    }
    lessons_placement_tests: PlacementTests[]
    lessons: {
      initial_lesson: Nullable<number>
      highest_lesson: Nullable<number>
      total_lessons: number
      total_lessons_mastered: number
      areas_of_weakness: AreasOfWeaknessItem[]
      areas_mastered: {
        category: string
        total_lessons_mastered: number
        lessons: { lesson: number }[]
      }[]
      recent_activity: {
        created_at: number
        lesson: number
        score: Nullable<number>
        attempts: number
      }[]
    }
    assessments: {
      average_score: Nullable<number>
      attempts: number
      quizzes_taken: number
      recent_activity: {
        created_at: number
        passed: boolean
        score: number
        map_number: number
      }[]
    }
    awards: Awards
    assignments: {
      recent_activity: (AssignmentsRecentActivityItemMs | AssignmentsRecentActivityDrivingTestItemMs)[]
    }
    mental_minute: {
      total_badges_earned: number
      total_correct: number
      recent_activity: {
        created_at: number
        lesson: number
        score: number
      }[]
    }
  }[]
}

export type SummaryStudentsMsData = SummaryStudentsMsResponse['summary']

export async function summaryStudentsMs(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: StudentScope,
  period: NormalizedInterval,
): Promise<Nullable<SummaryStudentsMsData>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SummaryStudentsMsResponse>(url, query)
  if (!data) return null

  const { summary } = data
  if (summary.length === 0) return null

  return summary
}
